/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2017,2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

const controlKeys = { Meta: 'metaKey', Control: 'ctrlKey', Alt: 'altKey', Shift: 'shiftKey' };

function generateMapKey(keyCodes, controlKeys) {
	return `${keyCodes
		.slice()
		.sort()
		.join(':')}::${controlKeys
		.slice()
		.sort()
		.join(':')}`;
}

function getKeyFromEvent(event, currentKeys) {
	const keyCodes = Object.keys(currentKeys).filter(k => currentKeys[k]);
	const pressedControlKeys = Object.keys(controlKeys).filter(k => event[controlKeys[k]]);

	return generateMapKey(keyCodes, pressedControlKeys);
}

function getKeyCode(e) {
	return e.key && e.key !== 'Unidentified' ? e.key : e.keyCode;
}

const keyBinder = () => {
	let boundElement;
	let enabled = false;
	const keyDownMap = {};
	let currentKeys = {};

	function keyDownListener(e) {
		const keyCode = getKeyCode(e);

		if (!controlKeys[keyCode]) {
			currentKeys[getKeyCode(e)] = true;
		}

		const key = getKeyFromEvent(e, currentKeys);

		const callback = keyDownMap[key];

		if (callback) {
			if (callback(e) !== false) {
				e.preventDefault();
			}
		}
	}

	function keyUpListener(e) {
		const key = getKeyCode(e);

		// Due to a "feature" in osx keyup events are not registered when Meta is held down
		if (key === 'Meta') {
			currentKeys = {};
		} else {
			currentKeys[getKeyCode(e)] = false;
		}
	}

	function focusListener() {
		currentKeys = {};
	}

	function updateListeners(add) {
		if (boundElement) {
			const fnName = add ? 'addEventListener' : 'removeEventListener';

			boundElement[fnName]('keydown', keyDownListener);
			boundElement[fnName]('keyup', keyUpListener);
			boundElement[fnName]('focusout', focusListener);
			boundElement[fnName]('focusin', focusListener);
		}
	}

	return {
		off(key) {
			return this.on(key, null);
		},

		on(keys, callback) {
			const allKeys = Array.isArray(keys) ? keys : [keys];

			const control = allKeys.filter(k => !!controlKeys[k]);
			const others = allKeys.filter(k => !controlKeys[k]);

			keyDownMap[generateMapKey(others, control)] = callback;

			return this;
		},

		setTarget(target) {
			// remove listeners
			enabled && updateListeners(false);

			// bind element
			boundElement = target;

			// re-add listeners
			target && enabled && updateListeners(true);

			return this;
		},

		enable(enable) {
			if (enable !== enabled) {
				updateListeners(enable);
			}
			enabled = enable;

			return this;
		},
	};
};

function isInputOrigin(e) {
	const { tagName } = e.target;

	if (tagName === 'TEXTAREA') {
		return true;
	}
	if (tagName === 'INPUT') {
		const type = e.target.type && e.target.type.toLowerCase();

		switch (type) {
			case 'email':
			case 'password':
			case 'number':
			case 'search':
			case 'tel':
			case 'text':
			case 'url':
				return true;
			default:
				return false;
		}
	}

	return false;
}

keyBinder.ignoreInputs = callback => e => {
	if (isInputOrigin(e)) {
		return false;
	}

	return callback(e);
};

export default keyBinder;
